import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/TwentyTwentysixContent.css";
import ResourcesHeroIntroSection from "../resources/ResourcesHeroIntroSection";
import ResourcesFilterBar from "../resources/ResourcesFilterBar";
import ResourcesSidebar from "../resources/ResourcesSidebar";
import ResourcesResultsSection from "../resources/ResourcesResultsSection";
import ResourcesFeaturedHero from "../resources/ResourcesFeaturedHero";
import ResourcesLatestSidebar from "../resources/ResourcesLatestSidebar";
import { resourcesArticles } from "../resources/resourcesStubData";
import { loadRssArticles } from "../resources/rssBlogLoader";

const INITIAL_DISPLAY_COUNT = 8;
const LOAD_MORE_STEP = 4;

export default function ResourcesPage() {
  const [articles, setArticles] = useState(resourcesArticles);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [categoryIds, setCategoryIds] = useState([]);
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadRssArticles()
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) setArticles(data);
        else setArticles(resourcesArticles);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("RSS load failed; falling back to stub data", err);
        if (!cancelled) setArticles(resourcesArticles);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(
    () => [...new Set((articles || []).map((a) => a.category).filter(Boolean))],
    [articles]
  );

  const filterState = useMemo(
    () => ({
      search: searchApplied,
      categoryIds,
      showFeaturedOnly,
    }),
    [searchApplied, categoryIds, showFeaturedOnly]
  );

  const filteredArticles = useMemo(() => {
    let list = articles || [];

    const q = (filterState.search || "").trim().toLowerCase();
    if (q) {
      list = list.filter((a) => {
        const haystack = [
          a.title,
          a.excerpt,
          a.category,
          (a.tags || []).join(" "),
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    if (filterState.categoryIds.length > 0) {
      list = list.filter((a) => filterState.categoryIds.includes(a.category));
    }

    if (filterState.showFeaturedOnly) {
      list = list.filter((a) => a.featured);
    }

    return list;
  }, [filterState]);

  const featuredArticle = filteredArticles.length > 0 ? filteredArticles[0] : null;
  const restArticles = filteredArticles.slice(1);
  const displayedArticles = restArticles.slice(0, displayCount);
  const hasMore = restArticles.length > displayCount;

  const hasActiveFilters =
    (searchApplied || "").trim() !== "" ||
    categoryIds.length > 0 ||
    showFeaturedOnly;

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchApplied(searchInput);
  };

  const handleClearAll = () => {
    setSearchInput("");
    setSearchApplied("");
    setCategoryIds([]);
    setShowFeaturedOnly(false);
  };

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + LOAD_MORE_STEP);
  };

  return (
    <div className="twentytwentysix-content">
      <div className="twentytwentysix-content__container">
        <ResourcesHeroIntroSection />

        {featuredArticle && (
          <section className="twentytwentysix-content__section">
            <ResourcesFeaturedHero article={featuredArticle} />
          </section>
        )}

        <div className="twentytwentysix-content__mlist-layout">
          <div className="twentytwentysix-content__mlist-sidebar-col">
            <ResourcesSidebar
              categoryIds={categoryIds}
              onCategoryIdsChange={setCategoryIds}
              showFeaturedOnly={showFeaturedOnly}
              onShowFeaturedOnlyChange={setShowFeaturedOnly}
              categories={categories}
              onClearAll={handleClearAll}
              hasActiveFilters={hasActiveFilters}
            />
            <ResourcesLatestSidebar articles={articles} />
          </div>

          <main className="twentytwentysix-content__mlist-main">
            <ResourcesFilterBar
              searchInput={searchInput}
              onSearchChange={setSearchInput}
              onSearchSubmit={handleSearchSubmit}
              onClearAll={handleClearAll}
              hasActiveFilters={hasActiveFilters}
            />
            <ResourcesResultsSection
              articles={displayedArticles}
              loading={loading}
              searchApplied={searchApplied}
              hasActiveFilters={hasActiveFilters}
            />
            {!loading && hasMore && (
              <div className="twentytwentysix-content__cta-row" style={{ marginTop: 24 }}>
                <button
                  type="button"
                  className="twentytwentysix-content__pill"
                  onClick={handleLoadMore}
                >
                  Load more
                </button>
              </div>
            )}
          </main>
        </div>

        <section className="twentytwentysix-content__section">
          <h2 className="twentytwentysix-content__subtitle">What you&apos;ll find</h2>
          <ul className="twentytwentysix-content__list">
            <li>Articles for moms in the thick of it</li>
            <li>Stories from our community</li>
            <li>Expert advice and roundtables</li>
            <li>Curated mama-products and services</li>
            <li>Blog contributions from members</li>
          </ul>
          <p className="twentytwentysix-content__copy">
            Browse our blog and resource library below, or explore the M List for vetted local businesses and services.
          </p>
          <div className="twentytwentysix-content__cta-row">
            <Link to="/twentytwentysix/mlist" className="twentytwentysix-content__pill">Browse the M List</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
